import * as signalR from "@microsoft/signalr";

export function novologikClient({
	onCallRequest,
	onConnected,
	onDisconnected,
	onReconnected
} = {}) {
	let connection = null;
	let currentJwt = null; // Saved for reconnection attempts
	let isConnecting = false;

	async function connect(jwt) {
		if (isConnecting) {
			return;
		}

		if (connection && connection.state !== signalR.HubConnectionState.Disconnected) {
			return;
		}

		isConnecting = true;

		try {
			console.log("[NOVOLOGIK] Establishing SignalR connection to:", process.env.VUE_APP_NOVOLOGIK_CONNECTOR_URL);

			currentJwt = jwt;
			
			connection = new signalR.HubConnectionBuilder()
				.withUrl(process.env.VUE_APP_NOVOLOGIK_CONNECTOR_URL, {
					accessTokenFactory: () => jwt
				})
				.withAutomaticReconnect()
				.build();

			connection.on("CallRequestReceived", (fullName, phone) => {
				onCallRequest?.(fullName, phone);
			});

			connection.onclose(async (error) => {
				console.warn("[NOVOLOGIK] Connection closed:", error?.message || "Unknown reason");

				onDisconnected?.();
			});

			connection.onreconnecting((error) => {
				console.warn("[NOVOLOGIK] Reconnecting:", error?.message || "Unknown reason");
			});

			connection.onreconnected(async (connectionId) => {
				console.log("[NOVOLOGIK] Reconnected:", connectionId);
				await onReconnected?.(connectionId);
			});

			await connection.start();
			console.log("[NOVOLOGIK] SignalR connection established");

			onConnected?.();
		} catch (error) {
			console.error("[NOVOLOGIK] Failed to establish connection:", error);
			onDisconnected?.();
		} finally {
			isConnecting = false;
		}
	}

	async function disconnect() {
		try {
			if (connection) {
				console.log("[NOVOLOGIK] Disconnecting");
				await connection.stop();
				connection = null;
				currentJwt = null;
				onDisconnected?.();
			}
		} catch (error) {
			console.error("[NOVOLOGIK] Error disconnecting:", error);
		}
	}

	async function registerGuestApp(upp, identifier) {
		try {
			if (!connection || connection.state !== signalR.HubConnectionState.Connected) {
				console.warn("[NOVOLOGIK] Cannot register, not connected");
				return;
			}

			console.log("[NOVOLOGIK] Registering guest app:", { upp, identifier });
			await connection.invoke("RegisterGuestApp", upp, identifier);
			console.log("[NOVOLOGIK] Guest app registered successfully");
		} catch (error) {
			console.error("[NOVOLOGIK] Failed to register guest app:", error);
		}
	}

	async function gotoPatient(upp, identifier, fullName, phone) {
		try {
			if (!connection || connection.state !== signalR.HubConnectionState.Connected) {
				console.warn("[NOVOLOGIK] Cannot goto patient, not connected");
				return false;
			}

			console.log("[NOVOLOGIK] Going to patient:", {
				upp,
				identifier,
				fullName,
				phone
			});

			const success = await connection.invoke(
				"GotoPatient",
				upp,
				identifier,
				fullName,
				phone
			);

			console.log("[NOVOLOGIK] GotoPatient result:", success);
			return success;
		} catch (error) {
			console.error("[NOVOLOGIK] Failed to goto patient:", error);
			return false;
		}
	}

	function isConnected() {
		return connection && connection.state === signalR.HubConnectionState.Connected;
	}

	return {
		connect,
		disconnect,
		registerGuestApp,
		gotoPatient,
		isConnected
	};
}
